import { Button } from '@edsn/pie-components';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { CancelCreateTicketDialog } from '#pie/components/cancel-create-ticket-dialog/CancelCreateTicketDialog';
import { InputCheckbox } from '#pie/components/input-checkbox/InputCheckbox';
import { PageHead } from '#pie/components/page-head/PageHead';
import { Stack } from '#pie/components/stack/Stack';
import { ToastButton } from '#pie/components/toast/Toast';
import { useToast } from '#pie/components/toast/ToastContext';
import { usePostTicket } from '#pie/internal/api/pie-bff';
import { useStoredParams } from '#pie/stores/searchParams';
import { rememberDuoGridAreaTickets } from '#pie/utils/duoGridAreaTickets';
import { Stepper } from '../../../components/stepper/Stepper';
import { codesSchema, TicketCreateEanCodes } from './TicketCreateEanCodes';
import {
  detailsSchema,
  TicketCreateEanDetails,
} from './TicketCreateEanDetails';
import { TicketCreateEanTickets } from './TicketCreateEanTickets';

export const eanSchema = z.object({
  ...codesSchema.shape,
  ...detailsSchema.shape,
  duoGridArea: z
    .object({
      electricityGridOperatorId: z.string(),
      gasGridOperatorId: z.string(),
    })
    .optional(),
});

export type PartialEanSchema = Partial<z.infer<typeof eanSchema>>;
interface EanFormContext {
  data: PartialEanSchema;
  setStepShouldBlock: (stepShouldBlock: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
}

export const EanFormContext = createContext<EanFormContext | undefined>(
  undefined,
);

export const useEanFormContext = () => {
  const context = useContext(EanFormContext);
  if (!context) {
    throw new Error('useEanFormContext must be used within a EanFormContext');
  }
  return context;
};

export interface StepProps {
  wizardActions: ReactNode;
  stepper: ReactNode;
  onSubmit: (data: PartialEanSchema) => void;
  onBack: () => void;
}

export const TicketCreateEan = () => {
  const { t } = useTranslation();
  const { t: transtic } = useTranslation('ticket');
  const params = useStoredParams('/berichtenbox');
  const [step, setStep] = useState(0);
  const [shouldBlock, setShouldBlock] = useState<[boolean, boolean, boolean]>([
    false,
    false,
    false,
  ]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<PartialEanSchema>({});
  const [createAnother, setCreateAnother] = useState<boolean>(false);
  const [isCreated, setIsCreated] = useState(false);

  const stepComponents = [
    TicketCreateEanCodes,
    TicketCreateEanTickets,
    TicketCreateEanDetails,
  ] as const;
  const Step = stepComponents[step];

  const navigate = useNavigate();
  const { addToast } = useToast();

  const {
    mutate,
    isPending: isPostLoading,
    isSuccess,
  } = usePostTicket({
    mutation: {
      onError(error) {
        addToast({
          message: transtic('create.toast.create_error.message'),
          title: transtic('create.toast.create_error.title'),
          type: 'error',
          error: error,
        });
      },
      onSuccess: ({ id }) => {
        addToast({
          action: (
            <ToastButton>
              <Link to={`/berichtenbox/${id}`}>
                {transtic('create.toast.create_success.action')}
              </Link>
            </ToastButton>
          ),
          message: transtic('create.toast.create_success.message', {
            title: data.title,
          }),
          title: transtic('create.toast.create_success.title'),
          type: 'success',
        });
      },
    },
  });
  const { mutateAsync: mutateDuoTicket, isPending: isDuoPostLoading } =
    usePostTicket();

  const submitDuoGridAreaTickets = async (
    baseTicket: Parameters<typeof mutateDuoTicket>[0]['data'],
    duoGridArea: NonNullable<PartialEanSchema['duoGridArea']>,
  ) => {
    try {
      const responses = await Promise.all([
        mutateDuoTicket({
          data: {
            ...baseTicket,
            gasEan: undefined,
            regionalGridOperatorId: duoGridArea.electricityGridOperatorId,
          },
        }),
        mutateDuoTicket({
          data: {
            ...baseTicket,
            electricityEan: undefined,
            regionalGridOperatorId: duoGridArea.gasGridOperatorId,
          },
        }),
      ]);

      rememberDuoGridAreaTickets(responses.map(({ id }) => id));

      [
        { ean: baseTicket.electricityEan, id: responses[0].id },
        { ean: baseTicket.gasEan, id: responses[1].id },
      ].forEach(({ ean, id }) => {
        addToast({
          action: (
            <ToastButton>
              <Link to={`/berichtenbox/${id}`}>
                {transtic('create.toast.create_success.action')}
              </Link>
            </ToastButton>
          ),
          message: transtic('create.toast.create_success.duo_message', {
            ean,
            title: baseTicket.title,
          }),
          title: transtic('create.toast.create_success.title'),
          type: 'success',
        });
      });
      setIsCreated(true);
    } catch {
      addToast({
        message: transtic('create.toast.create_error.message'),
        title: transtic('create.toast.create_error.title'),
        type: 'error',
      });
    }
  };

  const onSubmit = (stepData: PartialEanSchema) => {
    const mergedData = { ...data, ...stepData };
    setData(mergedData);

    if (step < stepComponents.length - 1) {
      setStep(step + 1);
    } else {
      const submitData = mergedData;
      const { files, eventDate, duoGridArea, ...ticketData } =
        eanSchema.parse(submitData);
      const baseTicket = {
        ...ticketData,
        attachments: files?.filter(({ file }) => file).map(({ file }) => file),
        eventDate: eventDate?.toString(),
      };

      if (!duoGridArea) {
        mutate({ data: baseTicket });
        return;
      }

      void submitDuoGridAreaTickets(baseTicket, duoGridArea);
    }
  };

  useEffect(() => {
    if (isSuccess || isCreated) {
      setIsCreated(true);
      navigate(createAnother ? '/berichtenbox/nieuw' : '/berichtenbox');
    }

    const searchParams = new URLSearchParams(location.search);
    const electricityEan = searchParams.get('electricityean');
    const gasEan = searchParams.get('gasean');
    if (electricityEan || gasEan) {
      setStep(2);
      setData((prev) => ({
        ...prev,
        electricityEan: electricityEan ?? prev.electricityEan,
        gasEan: gasEan ?? prev.gasEan,
      }));
    }
  }, [createAnother, isCreated, isSuccess, navigate]);

  return (
    <>
      <PageHead title={transtic('create.page_title')} />
      <EanFormContext.Provider
        value={{
          data,
          setIsLoading,
          setStepShouldBlock: (stepShouldBlock) => {
            const toReturn = [...shouldBlock] satisfies typeof shouldBlock;
            toReturn[step] = stepShouldBlock;
            setShouldBlock(toReturn);
          },
        }}
      >
        <Step
          onBack={() => setStep(step - 1)}
          onSubmit={onSubmit}
          stepper={
            <div className="-mx-6 mt-4 flex justify-center border-t border-t-border-divider px-6 pt-3">
              <Stepper
                steps={[
                  {
                    children: transtic('create.steps.codes'),
                  },
                  {
                    children: transtic('create.steps.tickets'),
                  },
                  {
                    children: t('common.ticket_details'),
                  },
                ].map((s, index) => ({
                  ...s,
                  active: index === step,
                  done: index < step,
                  onClick: index < step ? () => setStep(index) : undefined,
                }))}
              />
            </div>
          }
          wizardActions={
            step === stepComponents.length - 1 ? (
              <>
                <Button
                  as={Link}
                  to={`/berichtenbox${params}`}
                  action="tertiary"
                  emphasis="subtle"
                >
                  {t('common.button.cancel')}
                </Button>
                <Stack direction="row" className="items-center" gap="lg">
                  <InputCheckbox
                    label={transtic('create.footer.create_another')}
                    name="createAnother"
                    onCheckedChange={(value) => {
                      setCreateAnother(value === true);
                    }}
                  />
                  <Button
                    type="submit"
                    isLoading={isPostLoading || isDuoPostLoading || isLoading}
                  >
                    {t('common.submit')}
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <Button
                  as={Link}
                  to={`/berichtenbox${params}`}
                  action="tertiary"
                  emphasis="subtle"
                >
                  {t('common.button.cancel')}
                </Button>
                <Button
                  type="submit"
                  action="secondary"
                  emphasis="bold"
                  isLoading={isPostLoading || isDuoPostLoading || isLoading}
                >
                  {t('common.steps.next')}
                </Button>
              </>
            )
          }
        />
        <CancelCreateTicketDialog
          shouldBlock={
            shouldBlock.some((step) => step) && !isSuccess && !isCreated
          }
        />
      </EanFormContext.Provider>
    </>
  );
};
